import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { toast } from 'sonner'
import { UserTokenLimitForm } from '../../Forms/UserTokenLimitForm'
import { useCurrentOrganisation } from '../../useCurrentOrganisation'
import { restErrorMessage } from '../restErrorMessage'
import { type TokenLimitOverrideRow } from '../TokenLimitOverrideRow'

type OrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

export function RowActionsMenu({
  override,
  organisationUsers,
  existingOverrideUserIds,
}: {
  override: TokenLimitOverrideRow
  organisationUsers: OrganisationUser[]
  existingOverrideUserIds: number[]
}) {
  const { id: orgId } = useCurrentOrganisation()
  const { getSubscriptionsApiGetOrganisationTokenLimits, deleteSubscriptionsApiDeleteUserTokenLimit } =
    useRestClient()

  const invalidate = () =>
    getSubscriptionsApiGetOrganisationTokenLimits.invalidateQueries({
      parameters: { path: { orgId } },
    })

  const { mutate: deleteOverride } = deleteSubscriptionsApiDeleteUserTokenLimit.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      toast.success('Token limit override removed')
    },
    onError: (error) => toast.error(restErrorMessage(error, 'Failed to remove token limit override')),
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-8">
          <IconMenuOutline size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <UserTokenLimitForm
          selectedOverride={override}
          organisationUsers={organisationUsers}
          existingOverrideUserIds={existingOverrideUserIds}
          sheetTitle="Edit override"
          sheetDescription="Change this user's daily token limit in USD. Removing the override is done from the row actions."
          sheetTrigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconEditOutline size={16} />
                <span>Edit override</span>
              </div>
            </DropdownMenuItem>
          }
        />
        <ConfirmDialog
          title="Remove override"
          description="Remove this user's token limit override? They will use the organisation default, or have no daily cap if none is set."
          confirmVariant="destructive"
          confirmLabel="Remove"
          onConfirm={() => {
            deleteOverride({
              path: { orgId, userId: override.userId },
            })
          }}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconDeleteOutline size={16} />
                <span>Remove override</span>
              </div>
            </DropdownMenuItem>
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
